// ============================================================
// DuplicateCaseDialog.tsx
// "Duplicate case" — tick what to copy, create a new DRAFT case with the
// chosen data, then open it. Matches the approved mockup.
//
// DATA-MODEL NOTES (flagged in the investigation):
//  - A case ALWAYS belongs to a client (Case.clientId is non-nullable), so
//    the duplicate always links the SOURCE's client. The "Client" checkbox
//    controls whether the frozen client-SNAPSHOT fields are copied verbatim
//    (on) or left at the create default (off) — it can't make a client-less
//    case.
//  - Documents are OUT OF SCOPE here (copying R2 files needs a backend
//    endpoint that doesn't exist) — there is no Documents option at all.
//  - CLAIM TYPE (B2): required on every new case — pre-filled from the
//    source (with its route) and editable; a source without one must be
//    given one here.
//  - ALWAYS (not toggleable): new matter number (auto on create), status
//    Draft (setupFinalised=false / no closeDate — the create default, and we
//    never copy those), assignee cleared, history/activity fresh.
// ============================================================
import React, { useEffect, useState } from 'react'
import ModalFrame from './ModalFrame'
import { BTN_PRIMARY } from '../../styles/buttons'
import { BTN_CANCEL, HINT, SEC } from '../cases/caseEntryStyles'
import type { CaseSummary } from '../layout/CaseListScreen'
import { CASE_SECTION_GROUPS } from '../../utils/caseSectionGroups'
import {
  fetchCaseById, createCaseApi, updateCaseApi, updateCaseAssigneeApi,
  createWitnessApi, updateWitnessApi, createCaseDeadlineApi,
  listWitnessesApi, listCaseDeadlinesApi,
} from '../../api/casevoApi'
import { navigateTo } from '../../navigation/navigate'
import { serverErrorMessage } from '../../utils/serverError'
import ClaimTypeFields, { CLAIM_TYPE_MISSING, EMPTY_CLAIM_CHOICE, type ClaimChoice } from '../cases/ClaimTypeFields'
import { isClaimRoute, isClaimType, routeAllowedFor } from '../../utils/claimTypes'
import { refusedField } from '../../hooks/fieldRefusals'

interface DuplicateCaseDialogProps {
  isOpen: boolean
  onClose: () => void
  source: CaseSummary
  // Fired after the new case is created + opened (parent shows a toast).
  onDuplicated: (label: string) => void
  onError: (message: string) => void
}

// The copyable case-data sections (mirrors the mockup). Keys map 1:1 to
// CASE_SECTION_GROUPS. 'client' is separate (WHO); the rest are CASE DATA.
const CASE_DATA_SECTIONS = ['accident', 'injuries', 'defendant', 'medical', 'funding'] as const
// Dates the server checks against each other (DOB ↔ accident) — copied in
// their own PUTs, DOB first, so one refusal can't sink the rest of the copy.
const SEPARATE_DATES: string[] = ['clientDateOfBirth', 'accidentDate']

const DuplicateCaseDialog: React.FC<DuplicateCaseDialogProps> = ({ isOpen, onClose, source, onDuplicated, onError }) => {
  // Defaults: Client details ON, case-data ON, related records OFF.
  const [copyClient, setCopyClient] = useState(true)
  const [sections, setSections] = useState<Set<string>>(new Set(CASE_DATA_SECTIONS))
  const [copyWitnesses, setCopyWitnesses] = useState(false)
  const [copyDeadlines, setCopyDeadlines] = useState(false)
  const [busy, setBusy] = useState(false)
  // The new case's claim type (required) + route — the source's, once read.
  const [claim, setClaim] = useState<ClaimChoice>(EMPTY_CLAIM_CHOICE)
  const [claimError, setClaimError] = useState<string | null>(null)
  useEffect(() => {
    if (!isOpen) return
    let live = true
    setClaim(EMPTY_CLAIM_CHOICE)
    setClaimError(null)
    fetchCaseById(source.id).then((src) => {
      if (!live || !isClaimType(src.claimType)) return
      // Only if the person hasn't already picked one while it loaded. A route
      // that doesn't fit the type (an older case) isn't copied — the server
      // would refuse it on create.
      setClaim((cur) => (cur.claimType ? cur : { claimType: src.claimType as ClaimChoice['claimType'], claimRoute: isClaimRoute(src.claimRoute) && routeAllowedFor(src.claimRoute, src.claimType) ? src.claimRoute : '' }))
    }).catch(() => { /* the person picks one */ })
    return () => { live = false }
  }, [isOpen, source.id])

  const toggleSection = (key: string): void => {
    setSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleDuplicate = async (): Promise<void> => {
    if (!claim.claimType) { setClaimError(CLAIM_TYPE_MISSING); return }
    setClaimError(null)
    setBusy(true)
    // ── PHASE 1: source fetch + create. A failure HERE means no case was made
    //    → surface the error and stop (nothing partial to worry about). ──
    let createdId: string
    let srcFlat: Record<string, unknown>
    try {
      const src = await fetchCaseById(source.id)
      const clientId = src.client?.id
      if (!clientId) {
        onError('This case has no linked client, so it cannot be duplicated.')
        setBusy(false)
        return
      }
      srcFlat = src as unknown as Record<string, unknown>
      // Create the draft (new matter number + creator-assignee + Draft, server-
      // side). Client snapshot lands as the live-client pull.
      const created = await createCaseApi({ clientId, claimType: claim.claimType, claimRoute: claim.claimRoute || null })
      createdId = created.id
    } catch (err) {
      const refused = refusedField(err)
      if (refused?.field === 'claimType' || refused?.field === 'claimRoute') {
        setClaimError(refused.message)
        setBusy(false)
        return
      }
      onError('Failed to duplicate the case. Please try again.')
      setBusy(false)
      return
    }

    // ── PHASE 2: populate the created case (fields, assignee clear, related
    //    records). BEST-EFFORT: the case already exists, so a failure here
    //    still opens it — we just note the copy was partial rather than
    //    treating it as a total failure. ──
    let partial = false
    const skippedDates: string[] = []
    try {
      // Copy the ticked sections. 'client' copies the frozen client-snapshot
      // verbatim (overriding the create's live pull). We never copy
      // setupFinalised / closeDate / matterNumber / assignee → stays Draft
      // with its own matter number.
      const groupsToCopy = CASE_SECTION_GROUPS.filter(
        (g) => (g.key === 'client' ? copyClient : sections.has(g.key)),
      )
      const payload: Record<string, unknown> = {}
      const dates: { key: string; label: string; value: unknown }[] = []
      for (const g of groupsToCopy) {
        for (const f of g.fields) {
          if (SEPARATE_DATES.includes(f.key)) dates.push({ key: f.key, label: f.label, value: srcFlat[f.key] })
          else payload[f.key] = srcFlat[f.key]
        }
      }
      if (Object.keys(payload).length > 0) {
        await updateCaseApi(createdId, payload)
      }
      // ★ Dates go one PUT each, AFTER the rest: the server can refuse a date
      // (e.g. an accident before the client's date of birth on old data), and
      // in the same PUT that took every other copied field down with it.
      // A refused date is skipped and named in the toast.
      for (const d of dates) {
        if (d.value === null || d.value === undefined || d.value === '') continue
        try {
          await updateCaseApi(createdId, { [d.key]: d.value })
        } catch (err) {
          const why = serverErrorMessage(err, [400, 422])
          skippedDates.push(why ? `${d.label} (${why.replace(/\.$/, '')})` : d.label)
        }
      }
      // Clear the assignee (create set it to the creator).
      await updateCaseAssigneeApi(createdId, null)
      // Related records — create fresh on the new case.
      if (copyWitnesses) {
        const wits = await listWitnessesApi(source.id)
        for (const w of wits) {
          const nw = await createWitnessApi(createdId)
          await updateWitnessApi(nw.id, {
            name: w.name, addressLine1: w.addressLine1, addressLine2: w.addressLine2,
            addressTown: w.addressTown, addressCounty: w.addressCounty, addressPostcode: w.addressPostcode,
            phone: w.phone, email: w.email, statement: w.statement,
          })
        }
      }
      if (copyDeadlines) {
        const dls = await listCaseDeadlinesApi(source.id)
        for (const d of dls) {
          await createCaseDeadlineApi(createdId, {
            deadlineTypeId: d.deadlineType.id,
            // Civil date only — the deadline API now rejects a non-midnight
            // ISO (parseCivilDate), which would abort the copy loop.
            dueDate: typeof d.dueDate === 'string' ? d.dueDate.slice(0, 10) : d.dueDate,
            notes: d.notes || undefined,
          })
        }
      }
    } catch {
      partial = true
    }

    // ── PHASE 3: open the new draft case (same landing pattern as
    //    open-in-new-tab: clear the dashboard gate so ViewingPane renders it). ──
    try {
      // One history step: Back returns to where the duplicate was made.
      await navigateTo({ view: 'caseDetail', caseId: createdId })
    } catch {
      partial = true
    }
    const notCopied = skippedDates.length > 0 ? ` — not copied: ${skippedDates.join('; ')}` : ''
    onDuplicated(partial ? `Case duplicated (some data could not be copied)${notCopied}` : `Case duplicated${notCopied}`)
    onClose()
    setBusy(false)
  }

  const checkboxRow = 'flex items-center gap-[9px] text-[13.5px] text-casevo-forest cursor-pointer select-none'
  const checkbox = 'h-[18px] w-[18px] shrink-0 cursor-pointer accent-casevo-brand'
  const sectionLabel = (key: string): string => CASE_SECTION_GROUPS.find((g) => g.key === key)?.label ?? key

  // The newer modal style (ModalFrame + the case-entry classes), as New case.
  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      dismissible={!busy}
      eyebrow="Duplicate"
      eyebrowIcon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>}
      title="Duplicate case"
      subtitle="A new Draft case for the same client, with the parts you choose."
      footerLeft={<span className="text-[12px] text-casevo-text-soft"><b className="text-casevo-danger">*</b> required</span>}
      footerRight={
        <>
          <button type="button" onClick={onClose} disabled={busy} className={BTN_CANCEL}>Cancel</button>
          <button type="button" onClick={() => void handleDuplicate()} disabled={busy} className={`${BTN_PRIMARY} text-sm rounded-lg px-5 py-2.5`} aria-busy={busy}>
            {busy ? 'Creating…' : 'Create duplicate'}
          </button>
        </>
      }
    >
      <div className={SEC}>Claim</div>
      <div className="mb-[22px]">
        <ClaimTypeFields
          idPrefix="dup"
          value={claim}
          onChange={(next) => { setClaim(next); if (next.claimType) setClaimError(null) }}
          error={claimError}
          disabled={busy}
        />
      </div>

      <div className={SEC}>Client</div>
      <div className="mb-[22px]">
        <label className={checkboxRow}>
          <input type="checkbox" className={checkbox} checked={copyClient} onChange={(e) => setCopyClient(e.target.checked)} />
          Copy client details (snapshot as-is)
        </label>
        <p className={`${HINT} ml-[27px]`}>The duplicate is linked to the same client either way.</p>
      </div>

      <div className={SEC}>Case data</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-[22px]">
        {CASE_DATA_SECTIONS.map((key) => (
          <label key={key} className={checkboxRow}>
            <input type="checkbox" className={checkbox} checked={sections.has(key)} onChange={() => toggleSection(key)} />
            {sectionLabel(key)}
          </label>
        ))}
      </div>

      <div className={SEC}>Related records</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-[22px]">
        <label className={checkboxRow}>
          <input type="checkbox" className={checkbox} checked={copyWitnesses} onChange={(e) => setCopyWitnesses(e.target.checked)} />
          Witnesses
        </label>
        <label className={checkboxRow}>
          <input type="checkbox" className={checkbox} checked={copyDeadlines} onChange={(e) => setCopyDeadlines(e.target.checked)} />
          Deadlines
        </label>
      </div>

      <div className="rounded-[10px] border border-casevo-border-soft bg-casevo-input px-[14px] py-[10px] text-[12.5px] text-casevo-text-soft">
        Always: a new matter number is generated, status is set to <b className="text-casevo-forest">Draft</b>, the assignee is cleared, and history starts fresh.
      </div>
    </ModalFrame>
  )
}

export default DuplicateCaseDialog
